import React, { useRef, useState } from 'react';
import { uploadsAPI, hubError } from '../lib/api';
import { Button } from './ui/button';
import { Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const LIMITS = {
  image: { max: 8, accept: 'image/jpeg,image/png,image/webp,image/gif', label: 'JPG, PNG, WEBP o GIF · hasta 8 MB' },
  video: { max: 25, accept: 'video/mp4,video/quicktime,video/webm', label: 'MP4, MOV o WEBM · hasta 25 MB' },
};

export const MediaUploader = ({ kind = 'image', onUploaded, testId }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const limit = LIMITS[kind];

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > limit.max * 1024 * 1024) {
      toast.error(`El archivo pesa ${(file.size / 1048576).toFixed(1)} MB y el límite es ${limit.max} MB`);
      return;
    }
    if (!limit.accept.split(',').includes(file.type)) {
      toast.error(`Formato no permitido. Usa ${limit.label}`);
      return;
    }

    setProgress(0);
    try {
      const res = await (kind === 'video'
        ? uploadsAPI.video(file, setProgress)
        : uploadsAPI.image(file, setProgress));
      onUploaded(res.data || {});
      toast.success(kind === 'video' ? 'Video subido' : 'Imagen subida');
    } catch (err) {
      toast.error(hubError(err, 'No se pudo subir el archivo'));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={limit.accept}
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={progress !== null}
        data-testid={testId}
      >
        {progress !== null
          ? <><Loader2 className="w-4 h-4 spinner mr-1" /> Subiendo {progress}%</>
          : <><Upload className="w-4 h-4 mr-1" /> Subir archivo</>}
      </Button>
      <span className="text-[11px] text-muted-foreground">{limit.label}</span>
    </div>
  );
};

export const MediaPreview = ({ url, kind = 'image', onClear, testId }) => {
  if (!url) return null;
  return (
    <div className="relative inline-block mt-2" data-testid={testId}>
      {kind === 'video' ? (
        <video src={url} controls className="h-28 rounded-lg border" />
      ) : (
        <img src={url} alt="Vista previa" className="h-28 rounded-lg border object-cover" />
      )}
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
          data-testid={`${testId}-clear`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default MediaUploader;
